/**
 * Demo campaign performance that reconciles with the Overview.
 *
 * The Overview's marketing performance table is `buildChannels` over the demo
 * engine. Campaign figures are not generated separately: each channel's row —
 * its attributed revenue, orders and spend — is divided among the seeded
 * campaigns that use that channel, to the penny and to the order. Adding up
 * every campaign's Meta Ads row gives exactly the Overview's Meta Ads row for
 * the same filters, so no two screens can disagree.
 *
 * What the Overview does not show — entries, customers, link visits, QR scans,
 * clicks and promo-code uses — is derived from the channel's orders before the
 * split, so those add up across campaigns too. Campaigns created in the session
 * have had no traffic yet and read zero, as a new real campaign would.
 */

import { channelDrillDown, isChannelType, withRatios, type ChannelPerformanceRow, type PerformanceMetrics } from '@/api/campaigns'
import type { GlobalFilters } from '@/lib/filters'

import type { DemoDataset } from '../generators/engine'
import { buildChannels, type DemoChannelPerformance } from '../generators/overview'
import { unitNoise } from '../random'
import type { TenantFixture } from '../tenants/types'

const MARKETING_DIMENSIONS: readonly string[] = ['channel', 'campaign', 'creative']

/** A campaign view is scoped by the campaign itself, so the marketing dimensions do not apply to it. */
export function campaignViewFilters(filters: GlobalFilters): GlobalFilters {
  const dimensions = Object.fromEntries(
    Object.entries(filters.dimensions).filter(([dimension]) => !MARKETING_DIMENSIONS.includes(dimension)),
  )
  return { ...filters, dimensions }
}

/** Splits a whole number by weight (largest remainder), so the parts add up to it exactly. */
export function splitCount(total: number, weights: readonly number[]): number[] {
  const weightTotal = weights.reduce((sum, weight) => sum + weight, 0)
  if (weights.length === 0) return []
  if (weightTotal <= 0) return weights.map((_, index) => (index === 0 ? total : 0))
  const exact = weights.map((weight) => (total * weight) / weightTotal)
  const parts = exact.map(Math.floor)
  let remainder = total - parts.reduce((sum, part) => sum + part, 0)
  const order = exact
    .map((value, index) => ({ index, fraction: value - Math.floor(value) }))
    .sort((a, b) => b.fraction - a.fraction || a.index - b.index)
  for (const { index } of order) {
    if (remainder <= 0) break
    parts[index] = (parts[index] as number) + 1
    remainder -= 1
  }
  return parts
}

/** Splits an amount of money by weight, in pennies. */
export function splitMoney(total: number, weights: readonly number[]): number[] {
  return splitCount(Math.round(total * 100), weights).map((pennies) => pennies / 100)
}

type Activity = Omit<PerformanceMetrics, 'spend' | 'conversionRate' | 'costPerAcquisition' | 'returnOnAdSpend'>

/** A channel's whole activity for the period, from its Overview row. */
export function channelActivity(tenant: TenantFixture, row: DemoChannelPerformance): Activity {
  const orders = row.orders
  const noise = unitNoise(tenant.seed, 'campaign-interactions', row.entity.id)
  const offline = row.kind === 'offline'
  // Offline: people scan the QR code or type the short link; a few quote the promo code.
  const scans = offline && orders > 0 ? Math.round(orders / (0.07 + 0.03 * noise)) : 0
  const clicks = !offline && orders > 0 ? Math.round(orders / (0.03 + 0.02 * noise)) : 0
  return {
    visits: offline ? Math.round(scans * 0.2) : Math.round(clicks * 0.08),
    scans,
    clicks,
    promoCodeUses: Math.round(orders * (offline ? 0.35 : 0.05)),
    orders,
    entries: Math.round(orders * tenant.commerce.ticketsPerOrder),
    customers: Math.round(orders * tenant.commerce.customersPerOrder),
    revenue: row.attributedRevenue,
  }
}

/** Which seeded campaigns use each tenant channel, and how much of it each one drives. */
export function campaignWeights(tenant: TenantFixture): Map<string, Array<{ campaignId: string; weight: number }>> {
  const weights = new Map<string, Array<{ campaignId: string; weight: number }>>()
  for (const campaign of tenant.campaigns) {
    for (const channelId of campaign.channels) {
      const list = weights.get(channelId) ?? []
      list.push({ campaignId: campaign.id, weight: 0.5 + unitNoise(tenant.seed, 'campaign-share', campaign.id, channelId) })
      weights.set(channelId, list)
    }
  }
  return weights
}

const COUNT_FIELDS = ['visits', 'scans', 'clicks', 'promoCodeUses', 'orders', 'entries', 'customers'] as const

/**
 * Every seeded campaign's metrics per tenant channel id, for the filters'
 * period: `result.get(campaignId).get(channelId)`.
 */
export function seededPerformance(dataset: DemoDataset, filters: GlobalFilters): Map<string, Map<string, PerformanceMetrics>> {
  const { tenant } = dataset
  const rows = buildChannels(dataset, campaignViewFilters(filters)).data
  const weights = campaignWeights(tenant)
  const result = new Map<string, Map<string, PerformanceMetrics>>()

  for (const row of rows) {
    const users = weights.get(row.entity.id)
    if (!users) continue
    const shares = users.map((user) => user.weight)
    const activity = channelActivity(tenant, row)
    const counts = Object.fromEntries(COUNT_FIELDS.map((field) => [field, splitCount(activity[field], shares)])) as Record<
      (typeof COUNT_FIELDS)[number],
      number[]
    >
    const revenue = splitMoney(activity.revenue, shares)
    const spend = splitMoney(row.spend, shares)

    users.forEach((user, index) => {
      const metrics: PerformanceMetrics = {
        visits: counts.visits[index] ?? 0,
        scans: counts.scans[index] ?? 0,
        clicks: counts.clicks[index] ?? 0,
        promoCodeUses: counts.promoCodeUses[index] ?? 0,
        orders: counts.orders[index] ?? 0,
        entries: counts.entries[index] ?? 0,
        customers: counts.customers[index] ?? 0,
        revenue: revenue[index] ?? 0,
        spend: null,
        conversionRate: null,
        costPerAcquisition: null,
        returnOnAdSpend: null,
      }
      const byChannel = result.get(user.campaignId) ?? new Map<string, PerformanceMetrics>()
      byChannel.set(row.entity.id, withRatios(metrics, row.spend > 0 ? (spend[index] ?? 0) : null))
      result.set(user.campaignId, byChannel)
    })
  }
  return result
}

export function performanceRow(
  campaignId: string,
  channel: { id: string; name: string; channelType: string; kind: 'digital' | 'offline' },
  metrics: PerformanceMetrics,
): ChannelPerformanceRow {
  const channelType = isChannelType(channel.channelType) ? channel.channelType : 'custom'
  return {
    ...metrics,
    channelId: channel.id,
    name: channel.name,
    channelType,
    kind: channel.kind,
    drillDown: channelDrillDown(campaignId, channelType, channel.name),
  }
}
